import { ActiveManagementBadge } from "@/components/fund/active-management-badge";
import { DANE_TRACKED_FUNDS } from "@/data/dane-tracked-funds";
import type { Fund } from "@/data/funds";
import type { FundType } from "@/data/types";
import { formatAsOfMdy, formatAum, fundLiveAumDollars } from "@/hooks/use-fund-live-data";
import { fundImageSrcSet, fundImageUrl } from "@/lib/api-client";
import { typeDefaultCardImage } from "@/lib/fund-card-image";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { Link } from "react-router-dom";

/** Returns true only for safe http(s) URLs — never javascript:, data:, etc. */
export function isExternalRedirect(url: string | null | undefined): url is string {
  return typeof url === "string" && /^https?:\/\//i.test(url);
}

// Tooltip copy lifted verbatim from the static reference index.html.
const CATEGORY_TOOLTIPS: Partial<Record<FundType, string>> = {
  thematic:
    "A Thematic ETF seeks to provide targeted exposure to companies positioned to benefit from long-term structural trends and innovations, such as AI infrastructure, clean energy, or founder-led businesses.",
  leveraged:
    "A 2x Leveraged ETF seeks daily investment results that correspond to two times the daily performance of an underlying reference. Due to compounding, returns over periods longer than one day will differ from 2x the underlying.",
  buffer:
    "A Structured Buffer ETF seeks to provide upside participation up to a cap while buffering a defined amount of downside over an outcome period using FLEX Options.",
  fixed:
    "A Fixed Income ETF seeks income and capital preservation through a portfolio of debt securities with defined credit and maturity characteristics.",
};

const CATEGORY_PILL_LABEL: Record<FundType, string> = {
  thematic: "Thematic",
  leveraged: "2x Leveraged",
  buffer: "Buffer",
  fixed: "Fixed Income",
};

const PILL_TOOLTIP_BASE =
  "absolute top-[calc(100%+10px)] left-1/2 -translate-x-1/2 w-[250px] bg-[#131313] text-[var(--etf-white)] border border-[var(--etf-border)] rounded-md px-3.5 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.9)] font-[family-name:var(--font-geist)] text-[13px] font-normal leading-normal normal-case tracking-[0.02em] text-left whitespace-normal z-[100] pointer-events-none opacity-0 invisible transition-[opacity,visibility] duration-200 before:content-[''] before:absolute before:bottom-full before:left-1/2 before:border-solid before:border-transparent before:border-b-[var(--etf-border)] before:border-[7px] before:-ml-[7px] before:pointer-events-none after:content-[''] after:absolute after:bottom-full after:left-1/2 after:border-solid after:border-transparent after:border-b-[#131313] after:border-[6px] after:-ml-[6px] after:pointer-events-none";

function CatPillTooltip({ body }: { body: string }) {
  return (
    <span
      role="tooltip"
      className={cn(PILL_TOOLTIP_BASE, "group-hover/cat:opacity-100 group-hover/cat:visible")}
    >
      <span className="flex items-center justify-between mb-1">
        <strong className="text-[var(--etf-orange)] font-medium">Note</strong>
        <span className="opacity-50">×</span>
      </span>
      <span className="block text-[var(--etf-white2)]">{body}</span>
    </span>
  );
}

export function FundCard({ fund }: { fund: Fund }) {
  // DDB uses `fundType` (not `type`) and `id` (not `slug`).
  const fundType = fund.fundType;
  const categoryTooltip = CATEGORY_TOOLTIPS[fundType];
  // AUM is computed server-side from holdings.market_value (joined by ticker on
  // the list endpoint) — never the DDB record. Funds without a holdings snapshot
  // show "—" until their next dataload, no manual backfill needed.
  const liveAum = fundLiveAumDollars(fund);
  const aumDisplay = liveAum != null ? formatAum(liveAum) : null;
  // Label with the NAV-file date (daily_nav rate_date) so the card's AUM "as of"
  // matches the fund page's NAV / price / AUM date. Holdings-only funds (no NAV
  // row) fall back to the holdings position_date, matching their AUM value.
  const aumAsOf = formatAsOfMdy(fund.latestNavAsOfDate ?? fund.latestAsOfDate) ?? "—";
  const typeDefaultImage = typeDefaultCardImage(fundType);
  const remoteImage = fundImageUrl(fund.imageKeys[0]);
  const remoteSrcSet = fundImageSrcSet(fund.imageKeys[0]);

  // Bug #5: a DDB imageKey can resolve to a real CloudFront URL whose S3 object
  // is missing → the <img> 404s. On the first load error we swap to the bundled
  // type-default card image. `imageFailed` is a one-way latch so the fallback
  // (a bundled asset that always exists) can never re-trigger onError forever.
  const [imageFailed, setImageFailed] = useState(false);
  // Width-variant self-heal: a key the resizer backfill missed 404s on its
  // -333w/-666w candidate. First error while the srcset is active drops the
  // srcset (the browser retries the plain original); only a SECOND error —
  // the original itself broken — reaches the type-default latch above. Keys
  // without variants skip straight to the latch, exactly as before.
  const [variantsFailed, setVariantsFailed] = useState(false);

  // Show the Active/Passive management pill only for Dane-tracked non-buffer funds.
  // Buffer cards never show it (Dane rule); static-new-only slugs correctly omit it.
  const showStatusPill = fund.fundType !== "buffer" && DANE_TRACKED_FUNDS.has(fund.id);

  // Config-driven white-label redirect: any fund with a safe http(s)
  // whiteLabelRedirectUrl renders its whole card as an external <a> instead of
  // an internal <Link>. Defense-in-depth: isExternalRedirect rejects
  // javascript:/data:/other-scheme URLs even if they somehow reach the client.
  const redirectUrl = isExternalRedirect(fund.whiteLabelRedirectUrl)
    ? fund.whiteLabelRedirectUrl
    : undefined;

  const cardClass =
    "etf-fund-card group relative z-[1] hover:z-[50] overflow-visible flex flex-col no-underline text-inherit bg-[var(--etf-black)] transition-[background] duration-200 hover:bg-[var(--etf-black2)]";

  const innerContent = (
    <>
      <div
        className={cn(
          "etf-fund-card-image relative h-[200px] bg-[var(--etf-black)] flex flex-col justify-end px-[18px] py-4",
          "after:content-[''] after:absolute after:inset-0 after:bg-[linear-gradient(to_top,rgba(0,0,0,0.75)_0%,transparent_55%)] after:z-[1]",
        )}
      >
        <img
          src={remoteImage && !imageFailed ? remoteImage : typeDefaultImage}
          srcSet={remoteImage && !imageFailed && !variantsFailed ? remoteSrcSet : undefined}
          sizes={
            remoteImage && !imageFailed && !variantsFailed && remoteSrcSet
              ? "(max-width: 768px) 92vw, (max-width: 1024px) 46vw, 300px"
              : undefined
          }
          alt={`${fund.ticker} — ${fund.name}`}
          // Always lazy: the 100dvh hero puts every grid card below the fold,
          // so an eager card image only competes with the hero (the LCP
          // element) for bandwidth. The browser still loads any lazy image
          // that is actually in-viewport immediately, so a future layout
          // change can't strand a visible card.
          loading="lazy"
          // Latch on the first failure of the REMOTE image only; never re-arm
          // for the bundled fallback (which always exists), so no infinite loop.
          onError={
            remoteImage && !imageFailed
              ? () => {
                  if (remoteSrcSet && !variantsFailed) setVariantsFailed(true);
                  else setImageFailed(true);
                }
              : undefined
          }
          className="absolute inset-0 w-full h-full object-cover opacity-35 z-0 transition-opacity group-hover:opacity-[0.45]"
        />
        <div className="absolute top-3.5 right-3.5 flex items-center gap-1.5 z-[3] overflow-visible">
          {showStatusPill && <ActiveManagementBadge isActiveManagement={fund.isActiveManagement} />}
          <span
            className={cn(
              "etf-fc-cat font-[family-name:var(--font-instrument)] text-[10px] tracking-[0.12em] uppercase px-2.5 py-1 border border-solid",
              "text-[var(--etf-orange)] border-[rgba(255,85,0,0.5)] bg-[rgba(255,85,0,0.15)]",
              categoryTooltip && "group/cat relative cursor-help",
            )}
          >
            {CATEGORY_PILL_LABEL[fundType]}
            {categoryTooltip && <CatPillTooltip body={categoryTooltip} />}
          </span>
        </div>

        <div className="relative z-[2]">
          <div className="font-[family-name:var(--font-f37bolton)] text-[36px] leading-none tracking-[-0.02em] text-white">
            {fund.ticker}
          </div>
          <div className="font-[family-name:var(--font-geist)] text-[13px] font-medium text-white/65 mt-[5px]">
            {fund.name}
          </div>
        </div>
      </div>

      <div className="px-[18px] pt-[18px] pb-4 flex flex-col flex-1 border-t border-[var(--etf-border)] gap-4">
        <div className="flex">
          <div className="flex-1 min-w-0">
            <div className="font-[family-name:var(--font-instrument)] text-[11px] font-medium text-[var(--etf-white)] leading-none mb-1 whitespace-nowrap overflow-hidden text-ellipsis">
              {fund.expenseRatio}
            </div>
            <div className="font-[family-name:var(--font-instrument)] text-[7.5px] tracking-[0.1em] uppercase text-[var(--etf-white3)] leading-[1.4]">
              Expense Ratio
            </div>
          </div>
          <div className="flex-1 min-w-0 pl-3.5 border-l border-[var(--etf-border2)]">
            <div
              className={cn(
                "font-[family-name:var(--font-instrument)] text-[11px] font-medium text-[var(--etf-white)] leading-none mb-1 whitespace-nowrap overflow-hidden text-ellipsis",
                !aumDisplay && "text-[var(--etf-white3)] font-normal",
              )}
            >
              {aumDisplay ?? "—"}
            </div>
            <div className="font-[family-name:var(--font-instrument)] text-[7.5px] tracking-[0.1em] uppercase text-[var(--etf-white3)] leading-[1.4]">
              AUM{" "}
              <span className="text-[0.72em] opacity-60 font-normal block">(as of {aumAsOf})</span>
            </div>
          </div>
        </div>

        <span className="font-[family-name:var(--font-instrument)] text-[11.5px] tracking-[0.06em] py-[11px] px-4 border border-[var(--etf-border)] bg-transparent text-[var(--etf-white2)] cursor-pointer transition-all duration-[180ms] ease-in-out no-underline block text-center mt-auto group-hover:border-[var(--etf-orange)] group-hover:text-[var(--etf-orange)] group-hover:bg-[rgba(255,85,0,0.04)]">
          {redirectUrl ? "Visit site →" : "View Fund →"}
        </span>
      </div>
    </>
  );

  if (redirectUrl) {
    return (
      <a href={redirectUrl} rel="noopener noreferrer" className={cardClass}>
        {innerContent}
      </a>
    );
  }

  return (
    <Link to={`/${fund.id}`} className={cardClass}>
      {innerContent}
    </Link>
  );
}
